import { action, computed } from 'mobx'
import { orderBy } from 'lodash-es'
import { task } from 'mobx-task'
import { distinctUntilChanged, filter, merge, switchMap } from 'rxjs'
import { withAbortSignal } from '@/utils/rx-util'
import { toStream } from '@/utils/mobx-util'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { Revalidation } from '@/infra/revalidation'
import type { GetCurrentMemberFn, GetTeamMemberFn } from '@/features/iam'
import { type Job } from '@/features/jobs/Job'
import { type JobScopeChangeStore } from '@/features/jobs/job-scope-changes/JobScopeChangeStore'
import { type JobScopeChange } from '@/features/jobs/job-scope-changes/JobScopeChange'
import { type RequestScopeChangesDialogViewModel } from '@/features/jobs/details/components/RequestScopeChangesDialog/RequestScopeChangesDialogViewModel'
import { type ViewJobScopeChangeRequestDialogViewModel } from '@/features/jobs/details/components/ViewJobScopeChangeRequestDialog/ViewJobScopeChangeRequestDialogViewModel'
import { type ScopeChangeBannerViewModel } from '@/features/jobs/details/ScopeChangeBannerViewModel'
import { type UpdateJobScopeDialogViewModel } from '@/features/jobs/details/components/UpdateJobScopeDialog/UpdateJobScopeDialogViewModel'
import { type ServiceItemListDialogViewModel } from '@/features/jobs/details/components/ServiceItemListDialog/ServiceItemListDialogViewModel'

/**
 * A job's scope: its service items and the requests to change them.
 */
export class JobScopeChangesViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()
  private readonly _revalidation = new Revalidation(() => this.fetch.pending)

  constructor(
    private readonly _job: Job,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _getTeamMember: GetTeamMemberFn,
    private readonly _jobScopeChangeStore: JobScopeChangeStore,
    public readonly requestScopeChangesVm: RequestScopeChangesDialogViewModel,
    public readonly viewScopeChangeRequestVm: ViewJobScopeChangeRequestDialogViewModel,
    public readonly scopeChangeBannerVm: ScopeChangeBannerViewModel,
    public readonly updateJobScopeVm: UpdateJobScopeDialogViewModel,
    public readonly serviceItemListVm: ServiceItemListDialogViewModel,
  ) {
    this._lifecycle
      .manage(requestScopeChangesVm)
      .manage(updateJobScopeVm)
      .manage(
        merge(
          toStream(() => _job.status, true).pipe(distinctUntilChanged()),
          _jobScopeChangeStore.jobScopeChangeApproved$.pipe(
            filter((e) => e.jobId === _job.id),
          ),
        ).pipe(
          switchMap(() =>
            withAbortSignal((signal) =>
              // The scope change card renders a failure from the task's state.
              this.fetch(signal).catch(() => undefined),
            ),
          ),
        ),
      )
  }

  /**
   * Whether the scope change requests are refetching while the previous ones
   * stay on screen.
   */
  @computed
  get isRevalidating() {
    return this._revalidation.isRevalidating
  }

  /**
   * Job scope change requests.
   */
  @computed
  get scopeChangeRequests() {
    return this._jobScopeChangeStore.forJob(this._job)
  }

  @computed
  get scopeChangeRequestsNewestFirst() {
    return orderBy(this.scopeChangeRequests, 'createTime', 'desc')
  }

  /**
   * Leaves out the pending request, which the job's banner already shows.
   */
  @computed
  get resolvedScopeChangeRequestsNewestFirst() {
    return this.scopeChangeRequestsNewestFirst.filter((r) => !r.isPending)
  }

  /**
   * Whether the job's past requests, without the pending one, are worth
   * listing.
   */
  @computed
  get hasVisibleResolvedRequests() {
    return (
      this.canViewScopeChangeRequests &&
      this.resolvedScopeChangeRequestsNewestFirst.length > 0
    )
  }

  @computed
  get canChangeScope() {
    return this.canRequestScopeChanges || this.canUpdateJobScope
  }

  @computed
  get isBannerVisible() {
    return (
      this.canViewScopeChangeRequests &&
      Boolean(this.scopeChangeBannerVm.request)
    )
  }

  /**
   * Whether the job have a pending scope change request.
   */
  @computed
  get isScopeChangeRequestPending() {
    return this.scopeChangeRequests.some((jscr) => jscr.isPending)
  }

  /**
   * Whether the user can update the job scope.
   */
  @computed
  get canUpdateJobScope() {
    return this._isJobScopeUpdateAllowed && this._noScopeChangeIsOpen
  }

  /**
   * Whether we can request scope changes.
   */
  @computed
  get canRequestScopeChanges() {
    return this.isScopeChangeRequestAllowed && this._noScopeChangeIsOpen
  }

  /**
   * Whether the user can view the job scope change requests
   */
  @computed
  get canViewScopeChangeRequests() {
    return (
      this.isScopeChangeRequestAllowed ||
      this._isJobScopeUpdateAllowed ||
      this._isScopeChangeRequestListAllowed
    )
  }

  /**
   * Whether the job and the current user's permissions allow requesting scope
   * changes, whether or not a request is still open.
   */
  @computed
  get isScopeChangeRequestAllowed() {
    return (
      this._job.isJobScopeChangeRequestsEnabled &&
      ['Claimed', 'OnHold', 'Idle'].includes(this._job.status) &&
      this._permissions.jobScopeChangeCreate
    )
  }

  /**
   * The text we show when there is a pending action on the job.
   */
  @computed
  get pendingActionsText() {
    return this.isScopeChangeRequestPending
      ? 'Pending job scope changes'
      : 'Pending call completion'
  }

  /**
   * Returns the number of service items related to the job.
   */
  @computed
  get serviceItemsListLength() {
    return this.serviceItemListVm.serviceItems.length
  }

  @computed
  private get _isJobScopeUpdateAllowed() {
    return (
      this._job.isJobScopeChangeRequestsEnabled &&
      !this._job.completed &&
      !this._job.isDraft &&
      (this._permissions.adminJobScopeChangeManage ||
        this._permissions.adminJobScopeChangeResolve)
    )
  }

  @computed
  private get _isScopeChangeRequestListAllowed() {
    if (!this._job.isJobScopeChangeRequestsEnabled || this._job.isDraft) {
      return false
    }

    if (this._job.currentUserCollaborator) {
      return true
    }

    if (this._permissions.adminJobScopeChangeManage) {
      return true
    }

    const currentUserPublicId = this._getCurrentMember().user.publicId
    return this._job.teams.some(
      (team) =>
        this._getTeamMember(team.id, currentUserPublicId)?.permissions
          .viewTeamJobs,
    )
  }

  @computed
  private get _permissions() {
    return this._getCurrentMember(this._job.workspace.id).permissions
  }

  @computed
  private get _noScopeChangeIsOpen() {
    return this.scopeChangeRequests.every(
      (scopeChange) =>
        scopeChange.status === 'Accepted' ||
        scopeChange.status === 'Rejected' ||
        scopeChange.status === 'ApplyFailed',
    )
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  @action.bound
  viewScopeChangeRequests(scopeChangeRequest: JobScopeChange) {
    this.viewScopeChangeRequestVm.show(scopeChangeRequest)
  }

  /**
   * Loads the job's scope change requests.
   */
  fetch = task(async (signal?: AbortSignal) => {
    if (
      this._isScopeChangeRequestListAllowed ||
      this.isScopeChangeRequestAllowed ||
      this._isJobScopeUpdateAllowed
    ) {
      await this._jobScopeChangeStore.fetchScopeChangesForJob({
        jobId: this._job.id,
        signal,
      })
    }
    this._revalidation.markLoaded()
  })
}
